import React from 'react';
import {
  ArrowUpRight, BarChart3, Sigma, Share2, Search, Tag,
} from 'lucide-react';
import { EcosystemRing } from './EcosystemRing';
import { BrainGraph } from './BrainGraph';
import { ChatWorkflow } from './ChatWorkflow';

/* ---------- Mockup primitives (shared card illustration system) ---------- */

const Panel = ({ children }) => (
  <div className="bg-stone-100 rounded-3xl p-8 sm:p-12 flex items-center justify-center min-h-[420px]">
    {children}
  </div>
);

const extMeta = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return { label: 'PDF', bg: '#E0463A' };
  if (ext === 'xlsx' || ext === 'xls') return { label: 'XLS', bg: '#1E8E52' };
  if (ext === 'csv') return { label: 'CSV', bg: '#1E8E52' };
  if (ext === 'docx' || ext === 'doc') return { label: 'DOC', bg: '#2B7DE9' };
  return { label: 'FILE', bg: '#64748B' };
};

const Chip = ({ item }) => {
  if (item.type === 'file') {
    const m = extMeta(item.label);
    return (
      <span className="inline-flex items-center gap-2 bg-stone-100 rounded-lg pl-1 pr-2.5 py-1 text-[12px] text-stone-700 max-w-full">
        <span className="shrink-0 rounded-[5px] text-white font-bold leading-none flex items-center justify-center" style={{ background: m.bg, width: 18, height: 18, fontSize: 7 }}>{m.label}</span>
        <span className="truncate">{item.label}</span>
      </span>
    );
  }
  if (item.type === 'search') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-stone-100 rounded-lg px-2.5 py-1.5 text-[12px] text-stone-700">
        <Search className="w-3 h-3 text-stone-400 shrink-0" />{item.label}
      </span>
    );
  }
  if (item.type === 'graph') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-stone-100 rounded-lg px-2.5 py-1.5 text-[12px] text-stone-700">
        <Share2 className="w-3 h-3 text-stone-400 shrink-0" />{item.label}
      </span>
    );
  }
  if (item.type === 'avatar') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-stone-100 rounded-lg px-2 py-1 text-[12px] text-stone-700">
        <span className="w-4 h-4 rounded-full bg-gradient-to-br from-stone-300 to-stone-400 shrink-0" />{item.label}
      </span>
    );
  }
  return <span className="inline-flex items-center bg-stone-100 rounded-lg px-2.5 py-1.5 text-[12px] text-stone-700">{item.label}</span>;
};

const pillStyles = {
  orange: 'bg-gradient-to-r from-[#FBBF6B] to-[#F6A64C] text-[#7C2D12]',
  blue: 'bg-[#A9C9F8] text-[#1D4ED8]',
  purple: 'bg-[#D8BCF9] text-[#6B21A8]',
};

const Pill = ({ color, Icon, label }) => (
  <span className={`inline-flex items-center gap-1.5 ${pillStyles[color]} rounded-md px-2 py-0.5 text-[12px] font-medium whitespace-nowrap`}>
    {Icon && <Icon className="w-3 h-3" />}{label}
  </span>
);

const MockCard = ({ Icon, title, action: Action, stepLabel, steps }) => (
  <div className="w-full max-w-[620px] bg-white rounded-2xl border border-stone-200/80 shadow-[0_14px_44px_-18px_rgba(15,23,42,0.28)] overflow-hidden">
    {/* header */}
    <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-stone-100">
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-stone-700" />
        <span className="text-[15px] font-semibold text-stone-900">{title}</span>
      </div>
      <span className="w-7 h-7 rounded-lg bg-[#F58220] flex items-center justify-center text-white shrink-0">
        {Action && <Action className="w-3.5 h-3.5" />}
      </span>
    </div>

    {/* steps timeline */}
    <div className="p-5">
      {steps.map((s, i) => {
        const last = i === steps.length - 1;
        return (
          <div key={s.title} className="relative pl-6 pb-4 last:pb-0">
            <div className="text-[11px] text-stone-400 mb-1">{stepLabel} {i + 1}</div>
            <span className="absolute left-0 top-[22px] w-3 h-3 rounded-full border-2 border-stone-300 bg-white z-10" />
            {!last && <span className="absolute left-[5px] top-[22px] bottom-0 w-px bg-stone-200" />}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[14px] font-bold text-stone-900">{s.title}</span>
              {s.pill && <Pill {...s.pill} />}
            </div>
            {s.caption && <div className="text-[12px] text-stone-500 mb-2">{s.caption}</div>}
            {s.chips && (
              <div className="flex flex-wrap gap-1.5 mb-1">
                {s.chips.map((c, ci) => <Chip key={ci} item={c} />)}
              </div>
            )}
            {s.desc && <div className="text-[12px] text-stone-600 leading-relaxed">{s.desc}</div>}
            {s.progress && (
              <div className="mt-2.5 h-1 w-32 rounded-full bg-stone-200 overflow-hidden">
                <div className="h-full w-2/5 bg-stone-400 rounded-full" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

/* ---------- One card per product surface ---------- */

const WorkflowsViz = () => (
  <div className="w-full max-w-[620px] h-[340px] sm:h-[380px] rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-[0_14px_44px_-18px_rgba(15,23,42,0.28)]">
    <video
      autoPlay loop muted playsInline
      className="w-full h-full object-cover"
    >
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793342305/video-output-7A8FA3B1-408C-47AE-B8C6-40F63BC78BAB-1.mp4" type="video/mp4" />
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793342305/video-output-7A8FA3B1-408C-47AE-B8C6-40F63BC78BAB-1.mov" type="video/quicktime" />
    </video>
  </div>
);

const StudioViz = () => (
  <div className="w-full max-w-[620px] h-[340px] sm:h-[380px] rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-[0_14px_44px_-18px_rgba(15,23,42,0.28)]">
    <video
      autoPlay loop muted playsInline
      className="w-full h-full object-cover"
    >
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793343566/video-output-117B2C54-B8C6-4506-BBD1-346887AF41C1-1.mp4" type="video/mp4" />
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793343566/video-output-117B2C54-B8C6-4506-BBD1-346887AF41C1-1.mov" type="video/quicktime" />
    </video>
  </div>
);

const MemoViz = () => (
  <div className="w-full max-w-[620px] h-[340px] sm:h-[380px] rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-[0_14px_44px_-18px_rgba(15,23,42,0.28)]">
    <video
      autoPlay loop muted playsInline
      className="w-full h-full object-cover"
    >
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793343005/video-output-329A4A7C-0EA6-4B0F-B770-4C3D3F0FDF38-1.mp4" type="video/mp4" />
      <source src="https://res.cloudinary.com/s83adsj3/video/upload/v1793343005/video-output-329A4A7C-0EA6-4B0F-B770-4C3D3F0FDF38-1.mov" type="video/quicktime" />
    </video>
  </div>
);

const ModelViz = () => (
  <MockCard
    Icon={BarChart3} title="Financial Model" action={BarChart3} stepLabel="Step"
    steps={[
      { title: 'Ingest Core Assumptions', pill: { color: 'orange', Icon: BarChart3, label: 'Historical Data' }, caption: 'Loading 12 datasets',
        chips: [{ type: 'file', label: 'Profit_Loss_2025.xlsx' }, { type: 'file', label: 'Balance_Sheet.csv' }, { type: 'file', label: 'Contract_Data.csv' }] },
      { title: 'Run Financial Logic & Forecasting', pill: { color: 'blue', Icon: Sigma, label: 'Analytical Model' },
        chips: [{ type: 'search', label: 'Calculating: MOIC and returns' }, { type: 'search', label: 'Evaluating: Downside scenario v2' }] },
      { title: 'Deploy Model Outcomes', pill: { color: 'purple', Icon: Tag, label: 'Investor Model v1.1' },
        desc: 'Finalizing returns matrices and exporting models into IC memo format.', progress: true },
    ]}
  />
);

const ChatViz = () => <ChatWorkflow />;

/* ---------- Feature rows ---------- */

const features = [
  { eyebrow: 'Workflows', title: 'Run the entire deal pipeline, autonomously.', desc: 'End-to-end autonomous deal pipelines move a transaction from sourcing to IC verdict, each stage choreographed as a multi-agent DAG, with your team leading every gate.', Viz: WorkflowsViz },
  { eyebrow: 'Brain', title: 'A memory that grows with every deal.', desc: 'Unified transaction memory and cross-deal intelligence. Frontier records the people, companies, deals, and decisions that matter in a searchable, sovereign memory of how your firm works.', Viz: BrainGraph },
  { eyebrow: 'Studio', title: 'One cockpit for the whole deal team.', desc: 'A unified dealmaker cockpit for IC deliberations, with adversarial Bull vs. Bear analysis, 18-factor rubrics, and the full deal surface in a single zero-prompt workspace.', Viz: StudioViz },
  { eyebrow: 'Memo', title: 'Publication-grade memos in hours.', desc: 'Audit-grade strategic decks and IC memos synthesized at machine speed, every claim backed by cell-to-source lineage, ready for the board.', Viz: MemoViz },
  { eyebrow: 'Model', title: 'Deterministic math you can trust.', desc: 'Deterministic off-LLM valuation and return models. LBO, DCF, and debt schedules execute off-model with byte-level precision, with zero hallucinations.', Viz: ModelViz },
  { eyebrow: 'Chat', title: 'Interrogate the data room in plain language.', desc: 'Cell-grounded VDR interrogation. Ask any question across 1,400-file data rooms and get answers cited down to the exact source cell.', Viz: ChatViz },
];

// Lower Products section — minimal alternating product feature rows.
export const ProductFeatures = ({ onOpenBriefing }) => (
  <section className="py-24 sm:py-28 px-6 sm:px-10 lg:px-16 bg-[#FBFBFB] border-b border-stone-200/80">
    <div className="w-full max-w-[1500px] mx-auto">

      <div className="text-center max-w-2xl mx-auto mb-16 sm:mb-24">
        <span className="text-sm font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-3">The Product Suite</span>
        <h2 className="text-3xl sm:text-5xl font-medium tracking-tight text-stone-950">Six surfaces. One runtime.</h2>
      </div>

      <div className="space-y-20 sm:space-y-28">
        {features.map((f, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={f.eyebrow} id={f.eyebrow.toLowerCase()} className={`grid ${reverse ? 'lg:grid-cols-[0.62fr_1.38fr]' : 'lg:grid-cols-[1.38fr_0.62fr]'} gap-10 lg:gap-14 items-center scroll-mt-28`}>
              <div className={reverse ? 'lg:order-2' : ''}>
                <Panel><f.Viz /></Panel>
              </div>
              <div className={`${reverse ? 'lg:order-1' : ''} max-w-sm lg:px-2`}>
                <span className="text-sm font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-3">{f.eyebrow}</span>
                <h3 className="text-lg sm:text-xl font-bold text-stone-950 tracking-tight leading-snug">{f.title}</h3>
                <div className="h-px bg-stone-200 my-4" />
                <p className="text-[12px] sm:text-[13px] text-stone-600 leading-relaxed font-sans">{f.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Rotating integrations ring */}
      <div className="pt-20 sm:pt-28">
        <EcosystemRing />
      </div>

      {/* Closing CTA */}
      <div className="pt-16 sm:pt-20 text-center">
        <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-stone-950 mb-8">Deploy the platform.</h2>
        <button onClick={() => onOpenBriefing && onOpenBriefing('Platform Deployment')} className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-xs sm:text-sm font-semibold bg-[#090E1A] text-white hover:bg-black hover:scale-[1.02] transition-all shadow-sm">
          <span>Request Briefing</span>
          <ArrowUpRight className="w-4 h-4 text-[#F58220]" />
        </button>
      </div>
    </div>
  </section>
);
